import {
    Component,
    EventEmitter, HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {TdoDropdownV2Component} from '../tdo-dropdown-v2/tdo-dropdown-v2.component';
import {StaticDataService} from '../../../services/static-data.service';
import {EnumValidValueItemModel} from '../../../models/enum/enum-valid-value-item.model';
import {Overlay, OverlayRef} from '@angular/cdk/overlay';
import {fromEvent, Subscription} from 'rxjs';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {TemplatePortal} from '@angular/cdk/portal';
import {filter, take} from 'rxjs/operators';
import {EditEnumDialogComponent} from '../../../dialog/edit-enum-dialog/edit-enum-dialog.component';
import {EnumValidValueModel} from '../../../models/enum/enum-valid-value.model';
import { MessagingConstants } from 'src/app/models/MessagingConstants';
import { MessagingService } from 'src/app/services/messaging.service';

@Component({
    selector: 'app-tdo-enum-select',
    templateUrl: './tdo-enum-select.component.html',
    styleUrls: ['../tdo-dropdown-v2/tdo-dropdown-v2.component.scss', './tdo-enum-select.component.scss']
})
export class TdoEnumSelectComponent extends TdoDropdownV2Component implements OnInit, OnChanges, OnDestroy {
    @ViewChild('enumOptionsMenu') enumOptionsMenu: TemplateRef<any>;
    @ViewChild('main') mainDiv: HTMLDivElement;

    /**
     * name is the name of the enum to load valid value items from (e.g. enumOrganizationContactType)
     */
    @Input() name: string = '';
    /**
     * ValidValueItemId is how you set a persistent value that was set prior (from db / model)
     */
    @Input() validValueItemId: string = '';

    @Input() zVersion: string = 'B2';
    @Input() allowReset: boolean = false;
    @Input() isHideUnderline: boolean = false;

    @Output() isUserInput: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() enumSelected: EventEmitter<EnumValidValueItemModel> = new EventEmitter<EnumValidValueItemModel>();
    // @Output() enumList: any;


    validValue: EnumValidValueModel;
    validValueItems: EnumValidValueItemModel[];
    isContextOpen: boolean = false;
    // isUserInput: boolean = false;
    private validValueSubscription: Subscription;
    messagingSubscription: any;
    staticLabel: string;

    private overlayRef: OverlayRef | null;
    private sub: Subscription;

    @HostListener('contextmenu', ['$event'])
    onContextMenu(event: MouseEvent): void {
        this.onRightMouseClick(event);
    }

    constructor(private readonly staticDataService: StaticDataService,
                private readonly messagingService: MessagingService,
                private readonly dialog: MatDialog,
                private readonly overlay: Overlay,
                private readonly viewContainerRef: ViewContainerRef) {
        super();
    }

    ngOnInit(): void {
        this.selectionChange.subscribe((event) => {
            this.isUserInput.emit(event.isUserInput);
            if (event.isUserInput) {
                this.enumSelected.emit(event.value);
            }
        });
        this.configureEnums();
        if (this.zVersion === 'L1')  {
          this.staticLabel=  this.datasource.find(f => f.ValidValueItemId == this.validValueItemId).ItemDisplayValue;
        }
    }

    configureEnums(){
        if (this.staticDataService.isReady) {
            this.setupEnum();
        } else {
            this.validValueSubscription = this.staticDataService.validValuesReadyEvent.subscribe({
                complete: () => {
                    this.setupEnum();
                }
            });
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.validValueItemId?.currentValue && !this.isMultiSelect && this.validValueItems) {
            this.value = this.validValueItems.find(p => p.ValidValueItemId === this.validValueItemId);
            /// const val = this.enums.find(p => p.ValidValueItemId === this.ValidValueItemId);
            if (this.value) {
                this.selected.setValue(this.value);
            }
            else {
                if (this.allowReset) {
                    this.reset();
                }
            }
        }
        else {
            if (this.allowReset) {
                this.reset();
            }
        }
    }

    private setupEnum() {
        if (this.staticDataService.isReady) {
            this.validValue = this.staticDataService.validValues.find(p => p.ValidValueName.toLowerCase() === this.name.toLowerCase());
            this.validValueItems = this.staticDataService.getValidValueItemsByValidValueName(this.name);
            this.validValueItems.sort((a, b) => a.ItemOrder - b.ItemOrder);
            this.datasource = this.validValueItems;
            this.displayProperty = 'ItemDisplayValue';
            if (this.validValueItemId?.length > 0) {
                this.value = this.validValueItems.find(p => p.ValidValueItemId === this.validValueItemId);
                this.selected.setValue(this.value);
                this.enumSelected.emit(this.value as EnumValidValueItemModel);
            }
        }
        else {
            if (!this.validValueSubscription) {
                this.validValueSubscription = this.staticDataService.validValuesReadyEvent.subscribe({
                    complete: () => {
                        this.setupEnum();
                    }
                });
                this.staticDataService.refreshValues(true, false, false);
            }
        }
    }

    onRightMouseClick(event: MouseEvent) {
        if (this.validValue.isSystem) {
            return;
        }
        event.preventDefault();
        this.openContext(event, this.enumOptionsMenu, this.validValueItems);
    }

    openContext({ x, y }: MouseEvent, template: TemplateRef<any>, data: any, closeOnItemClick = true): void {
        this.isContextOpen = true;
        this.closeContext();
        x = x + 50;
        y = y - 20;
        const positionStrategy = this.overlay.position().flexibleConnectedTo({ x, y }).withPositions([
            {
                originX: 'end',
                originY: 'bottom',
                overlayX: 'end',
                overlayY: 'top'
            }
        ]);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.close()
        });

        this.overlayRef.attach(new TemplatePortal(template, this.viewContainerRef, { $implicit: data }));

        this.sub = fromEvent<MouseEvent>(document, 'click')
            .pipe(
                filter(event => {
                    if (closeOnItemClick) {
                        this.closeContext();
                    } else {
                        const clickTarget = event.target as HTMLElement;
                        return !!this.overlayRef && !this.overlayRef.overlayElement.contains(clickTarget);
                    }
                }), take(1)).subscribe(() => this.closeContext());
    }

    closeContext(): void {
        this.sub !== undefined && this.sub.unsubscribe();
        if (this.overlayRef) {
            this.isContextOpen = false;
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
    }

    onEditList() {
        const config = new MatDialogConfig();
        config.panelClass = 'dialog';
        config.data = this.validValueItems;

        this.dialog.open(EditEnumDialogComponent, config).afterClosed().subscribe((res) => {
            if (res) {
                this.validValueItems = [];


                //console.log('selected value', this.selected.value);

                setTimeout(() => {
                    this.validValueItems = res.filter(f=> f.xauditDeleted ==false);
                    this.datasource = this.validValueItems;



                }, 100);
            }
        });
    }

    onRefreshList() {
    }

    reset() {
        this.selected.setValue(null);
        this.validValueItemId = '';
        this.value = null;
    }

    ngOnDestroy() {
        if (this.validValueSubscription)
        {this.validValueSubscription.unsubscribe(); }
    }

    onClearValue() {
        this.reset();

        const z = new EnumValidValueItemModel();
        z.ValidValueItemId = null;
        z.ValidValueId =  null;
        z.ItemDisplayValue = '';

        this.enumSelected.emit(z);
    }
}
